import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfigLimits } from '../api/hooks';
import './LimitsConfigPage.css';

const PAGE_SIZE = 100;

// Every column except the numeric Value gets its own multi-select filter.
const COLUMNS = [
  { key: 'scope', label: 'Scope' },
  { key: 'sport', label: 'Sport' },
  { key: 'market', label: 'Market' },
  { key: 'limit_type', label: 'Limit' },
  { key: 'user_tier', label: 'Tier' },
  { key: 'line_game_status', label: 'Game' },
  { key: 'line_type', label: 'Line' },
  { key: 'season_type', label: 'Season' },
  { key: 'player_id', label: 'Player' },
  { key: 'leg_count', label: 'Legs' },
  { key: 'promo_type', label: 'Promo' },
];

const NULL_OPT = '\u0000'; // internal sentinel for NULL/blank so it can be selected like any value
const norm = (v) => (v === null || v === undefined || v === '' ? NULL_OPT : String(v));
const optLabel = (v) => (v === NULL_OPT ? '(none)' : v);

function usd(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toLocaleString()}`;
}
function cell(v) {
  return v === null || v === undefined || v === '' ? '—' : String(v);
}
function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  return err?.message || 'Request failed';
}

/** Column filter: a dropdown of the column's distinct values, any number selectable (checkboxes). */
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const toggle = (val) => {
    const set = new Set(selected);
    if (set.has(val)) set.delete(val); else set.add(val);
    onChange([...set]);
  };
  const count = selected.length;
  return (
    <div className="ms" ref={ref}>
      <button
        type="button"
        className={`filter-select ms-trigger${count ? ' ms-trigger--active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {label}{count ? ` · ${count}` : ''} <span className="ms-caret">▾</span>
      </button>
      {open && (
        <div className="ms-menu">
          <div className="ms-menu-head">
            <span className="subtle">{label}</span>
            {count > 0 && <button type="button" className="ms-clear" onClick={() => onChange([])}>clear</button>}
          </div>
          <div className="ms-options">
            {options.length === 0 ? (
              <div className="subtle ms-empty">no values</div>
            ) : (
              options.map((v) => (
                <label key={v} className="ms-option">
                  <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
                  <span>{optLabel(v)}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Full config_limits view — the live, fanned source of truth. No filter required to load; the
 * config_limits view already reflects every override the instant it's written (and refetches after
 * Sync to BQ, which invalidates the configLimits cache). Defaults to the base + 2.0-tier collapse
 * the backend applies when no user_tier is set, so the table isn't the full cartesian blow-up.
 */
export default function LimitsTablePage() {
  const [filters, setFilters] = useState({}); // { colKey: [normalized values] }
  const [page, setPage] = useState(0);

  const ROW_CAP = 10000; // backend hard max
  const query = useConfigLimits({ limit: ROW_CAP });
  const rows = useMemo(() => query.data?.rows || [], [query.data]);
  const cappedOut = rows.length >= ROW_CAP;

  // Distinct, sorted values per column (drives each dropdown).
  const optionsByCol = useMemo(() => {
    const out = {};
    COLUMNS.forEach((c) => {
      const set = new Set();
      rows.forEach((r) => set.add(norm(r[c.key])));
      out[c.key] = [...set].sort((a, b) => {
        if (a === NULL_OPT) return 1; // push (none) to the bottom
        if (b === NULL_OPT) return -1;
        return a.localeCompare(b, undefined, { numeric: true });
      });
    });
    return out;
  }, [rows]);

  const filtered = useMemo(() => {
    const active = COLUMNS.filter((c) => (filters[c.key] || []).length > 0);
    if (active.length === 0) return rows;
    return rows.filter((r) => active.every((c) => filters[c.key].includes(norm(r[c.key]))));
  }, [rows, filters]);

  const anyFilter = COLUMNS.some((c) => (filters[c.key] || []).length > 0);
  const setColFilter = (key, vals) => { setFilters((f) => ({ ...f, [key]: vals })); setPage(0); };
  const clearAll = () => { setFilters({}); setPage(0); };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <div className="limits-page">
      <div className="page-header">
        <div>
          <h1>config_limits <span className="subtle">(live view)</span></h1>
          <p className="subtle">
            The full <strong>config_limits</strong> view — always current (reflects overrides the moment
            they’re written; refetches after Sync to BQ). Showing base + tier&nbsp;2.0; use the{' '}
            <Link to="/manual/limits">Limits page</Link> filters to inspect other user tiers.
          </p>
        </div>
        <div className="limits-header-actions">
          <Link to="/manual/limits" className="button">← Back to Limits</Link>
          <button type="button" className="button button-primary" onClick={() => query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="limits-card-head">
          <h2>All limits</h2>
          <span className="subtle">
            {query.isLoading
              ? 'loading…'
              : `${filtered.length.toLocaleString()}${anyFilter ? ` of ${rows.length.toLocaleString()}` : ''} row(s)`}
            {cappedOut ? ` · capped at ${ROW_CAP.toLocaleString()} — filter to narrow` : ''}
          </span>
        </div>
        <div className="limits-filter-bar">
          {COLUMNS.map((c) => (
            <MultiSelect
              key={c.key}
              label={c.label}
              options={optionsByCol[c.key] || []}
              selected={filters[c.key] || []}
              onChange={(vals) => setColFilter(c.key, vals)}
            />
          ))}
          {anyFilter && <button type="button" className="button button-small" onClick={clearAll}>Clear all</button>}
        </div>
        <div className="table-container">
          <table className="table limits-table">
            <thead>
              <tr><th>Scope</th><th>Sport</th><th>Market</th><th>Limit</th><th>Tier</th><th>Game</th><th>Line</th><th>Season</th><th>Player</th><th>Legs</th><th>Promo</th><th>Value</th></tr>
            </thead>
            <tbody>
              {query.isLoading ? (
                <tr><td colSpan={12} className="subtle">Loading…</td></tr>
              ) : query.isError ? (
                <tr><td colSpan={12} className="limits-cell-error">Failed to load config_limits: {formatApiError(query.error)}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={12} className="subtle">{anyFilter ? 'No rows match the selected filters.' : 'No rows.'}</td></tr>
              ) : (
                paged.map((r, i) => (
                  <tr key={i}>
                    <td>{cell(r.scope)}</td><td>{cell(r.sport)}</td><td>{cell(r.market)}</td><td>{cell(r.limit_type)}</td>
                    <td>{cell(r.user_tier)}</td><td>{cell(r.line_game_status)}</td><td>{cell(r.line_type)}</td>
                    <td>{cell(r.season_type)}</td><td>{cell(r.player_id)}</td><td>{cell(r.leg_count)}</td><td>{cell(r.promo_type)}</td>
                    <td className="mono">{usd(r.value_usd)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > PAGE_SIZE && (
          <div className="limits-filter-bar" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="button button-small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>Prev</button>
            <span className="subtle">Page {safePage + 1} of {pageCount}</span>
            <button type="button" className="button button-small" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
